/**
 * Génération PNG/PDF poster via Canvas 2D — rendu professionnel
 */

const W = 800;
const H = 1120;
const PAD = 40;
const RADIUS = 12;

const TEMPLATE_COLORS = {
  amber: {
    bg: ["#d97706", "#b45309", "#92400e"],
    accent: "rgba(255,255,255,0.15)",
    accentBorder: "rgba(255,255,255,0.25)",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.85)",
  },
  slate: {
    bg: ["#1e293b", "#0f172a", "#020617"],
    accent: "rgba(255,255,255,0.1)",
    accentBorder: "rgba(255,255,255,0.18)",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.8)",
  },
  emerald: {
    bg: ["#047857", "#065f46", "#064e3b"],
    accent: "rgba(255,255,255,0.12)",
    accentBorder: "rgba(255,255,255,0.22)",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.9)",
  },
  rose: {
    bg: ["#be123c", "#9f1239", "#881337"],
    accent: "rgba(255,255,255,0.12)",
    accentBorder: "rgba(255,255,255,0.22)",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.9)",
  },
};

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    const m = ctx.measureText(test);
    if (m.width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function drawPosterCanvas({
  recipe,
  templateId,
  userName,
  imageDataUrl,
  logoDataUrl,
}) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  const c = TEMPLATE_COLORS[templateId] || TEMPLATE_COLORS.amber;

  // Fond dégradé (3 stops pour plus de profondeur)
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, c.bg[0]);
  grad.addColorStop(0.5, c.bg[1] || c.bg[0]);
  grad.addColorStop(1, c.bg[2] || c.bg[1] || c.bg[0]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  let y = PAD;

  // En-tête — logo ou texte
  const logoH = 56;
  const logoImg = logoDataUrl ? await loadImage(logoDataUrl) : null;
  if (logoImg) {
    const logoW = (logoImg.width / logoImg.height) * logoH;
    const logoX = PAD;
    const logoY = y;
    ctx.drawImage(logoImg, logoX, logoY, logoW, logoH);
  } else {
    ctx.fillStyle = c.text;
    ctx.font = "bold 26px system-ui, sans-serif";
    ctx.fillText("JesCook", PAD, y + 22);
  }
  ctx.font = "10px system-ui, sans-serif";
  ctx.fillStyle = c.muted || "rgba(255,255,255,0.75)";
  ctx.textAlign = "right";
  ctx.fillText("Poster généré par JesCook", W - PAD, y + 22);
  ctx.textAlign = "left";
  y += Math.max(44, logoH + 8);

  // Image recette (coins arrondis)
  const imgW = W - PAD * 2;
  const imgH = Math.round(imgW * (3 / 4));
  const imgUrl = imageDataUrl || recipe.image_url || recipe.image;

  ctx.save();
  roundRect(ctx, PAD, y, imgW, imgH, RADIUS);
  ctx.clip();

  if (imgUrl) {
    const img = await loadImage(imgUrl);
    if (img) {
      const ratio = Math.min(imgW / img.width, imgH / img.height);
      const sw = img.width * ratio;
      const sh = img.height * ratio;
      const sx = PAD + (imgW - sw) / 2;
      ctx.drawImage(img, sx, y, sw, sh);
    } else {
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.fillRect(PAD, y, imgW, imgH);
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.font = "18px system-ui";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("📷 Image", PAD + imgW / 2, y + imgH / 2);
      ctx.textBaseline = "alphabetic";
      ctx.textAlign = "left";
    }
  } else {
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.fillRect(PAD, y, imgW, imgH);
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "48px system-ui";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🍳", PAD + imgW / 2, y + imgH / 2);
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
  }

  ctx.restore();

  // Fine bordure autour de l'image
  ctx.strokeStyle = c.accentBorder || "rgba(255,255,255,0.2)";
  ctx.lineWidth = 1;
  roundRect(ctx, PAD, y, imgW, imgH, RADIUS);
  ctx.stroke();

  y += imgH + 32;

  // Titre
  ctx.fillStyle = c.text;
  ctx.font = "bold 30px system-ui, sans-serif";
  const title = recipe.title || "Sans titre";
  const titleLines = wrapText(ctx, title, imgW);
  const titleLineH = 38;
  for (let i = 0; i < Math.min(titleLines.length, 2); i++) {
    ctx.fillText(titleLines[i], PAD, y + 28);
    y += titleLineH;
  }
  y += 20;

  // Métadonnées (ligne unique, bien espacée)
  ctx.font = "15px system-ui, sans-serif";
  ctx.fillStyle = c.muted || "rgba(255,255,255,0.9)";
  const infos = [];
  if (recipe.prep_time != null) infos.push(`⏱ ${recipe.prep_time} min`);
  if (recipe.cook_time != null) infos.push(`🔥 ${recipe.cook_time} min`);
  if (recipe.servings != null) infos.push(`👥 ${recipe.servings}`);
  if (recipe.difficulty) infos.push(`⭐ ${recipe.difficulty}`);
  if (infos.length) {
    ctx.fillText(infos.join("   ·   "), PAD, y);
    y += 40;
  }

  // Bloc Ingrédients (coins arrondis, bordure légère)
  const ingreds = recipe.ingredients
    ? String(recipe.ingredients)
        .split("\n")
        .filter((l) => l.trim())
        .slice(0, 5)
    : [];

  if (ingreds.length > 0) {
    const boxPad = 20;
    const ingLineH = 26;
    const headerH = 36;
    const boxPadBottom = 22;
    let ingTotalH = headerH;
    ctx.font = "15px system-ui, sans-serif";
    const maxIngW = imgW - boxPad * 2;
    for (const ing of ingreds) {
      const lines = wrapText(ctx, `• ${ing}`, maxIngW);
      ingTotalH += Math.min(lines.length, 2) * ingLineH;
    }
    const boxH = Math.min(240, ingTotalH + boxPad + boxPadBottom);

    roundRect(ctx, PAD, y, imgW, boxH, RADIUS);
    ctx.fillStyle = c.accent;
    ctx.fill();
    ctx.strokeStyle = c.accentBorder || "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    ctx.stroke();

    y += boxPad + 18;
    ctx.fillStyle = c.text;
    ctx.font = "bold 11px system-ui, sans-serif";
    ctx.fillText("INGRÉDIENTS", PAD + boxPad, y);
    y += 30;

    ctx.font = "15px system-ui, sans-serif";
    ctx.fillStyle = c.muted || "rgba(255,255,255,0.95)";
    for (const ing of ingreds) {
      const lines = wrapText(ctx, `• ${ing}`, maxIngW);
      for (let i = 0; i < Math.min(lines.length, 2); i++) {
        ctx.fillText(lines[i], PAD + boxPad, y);
        y += ingLineH;
      }
    }
    y += boxPadBottom;
  }

  // Footer
  const footerTop = H - PAD - 44;
  ctx.strokeStyle = c.accentBorder || "rgba(255,255,255,0.15)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD, footerTop);
  ctx.lineTo(W - PAD, footerTop);
  ctx.stroke();

  ctx.fillStyle = c.muted || "rgba(255,255,255,0.65)";
  ctx.font = "12px system-ui, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText(
    `Créé par ${userName || "JesCook"}  ·  JesCook`,
    PAD,
    footerTop + 12,
  );
  ctx.textAlign = "right";
  ctx.fillText("JesCook.com", W - PAD, footerTop + 12);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  return canvas;
}
